import { useGlobalContext } from '~/context/GlobalContext';
import { apiService } from '~/services/apiService';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ChatIcon, UsersIcon } from '../Icons/Icons';
import { IChatRoom } from '~/types/common';
import { useViewportResize } from '~/hooks/useViewportResize';
import classNames from 'classnames';
import styles from './RoomContainer.module.css';

function RoomContainer() {
  const { context, setContext } = useGlobalContext();
  const resize = useViewportResize();
  const isActive = context.activeSidebarId === "rooms";
  const roomContainerRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const INITIAL_COUNT = 20;
  const BATCH_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  // Resetowanie stanu i pozycji scrolla
  useEffect(() => {
    if (!isActive) {
      // Gdy wychodzimy z zakładki:
      setVisibleCount(INITIAL_COUNT);
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = 0; // Wymuszenie powrotu na górę
      }
    }
  }, [isActive]);

  // Resetowanie przy wyszukiwaniu
  useEffect(() => {
    setVisibleCount(INITIAL_COUNT);
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
  }, [context.searchQuery]);

  const getRooms = async () => {
    try {
      const data = await apiService('/chat/rooms');
      setContext({ publicRooms: data || [] });
    } catch (error) {
      console.error('Błąd podczas pobierania pokoi:', error);
      setContext({ publicRooms: [] });
    }
  };

  const changeRoom = (room: IChatRoom) => {
    window.dispatchEvent(new CustomEvent('changeRoom', {
      bubbles: true,
      detail: room,
    }));
  };

  useEffect(() => {
    if (!context.publicRooms?.length) {
      getRooms();
    }
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isActive) {
      const requestCounts = () => {
        window.dispatchEvent(new CustomEvent('getPeopleCount', {
          bubbles: true,
          detail: { timestamp: Date.now() },
        }));
      };
      requestCounts();
      interval = setInterval(requestCounts, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive]);

  const allFilteredRooms = useMemo(() => {
    const query = context.searchQuery?.toLowerCase().trim() || '';
    const rooms = context.publicRooms || [];
    if (!query) return rooms;
    return rooms.filter((room: any) => room.name.toLowerCase().includes(query));
  }, [context.publicRooms, context.searchQuery]);

  const visibleRooms = useMemo(() => {
    return allFilteredRooms.slice(0, visibleCount);
  }, [allFilteredRooms, visibleCount]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && visibleCount < allFilteredRooms.length) {
        setVisibleCount((prev) => prev + BATCH_SIZE);
      }
    }, {
      rootMargin: '150px',
    });

    if (node) observer.current.observe(node);
  }, [visibleCount, allFilteredRooms.length]);

  return (
    <div
      ref={roomContainerRef}
      className={classNames(styles.roomContainer, {
        [styles.active]: isActive,
      })}
      style={{
        transform: resize.isKeyboardOpen
          ? `translateY(${(resize.initialHeight ?? resize.height) - resize.height}px)`
          : 'translateY(0)'
      }}>
      <div className={styles.listHeader}>
        <div className={styles.listHeaderRoom}>Pokojów: </div>
        <div className={styles.listHeaderInfo}>
          {allFilteredRooms.length}
        </div>
      </div>

      <div ref={scrollAreaRef} className={styles.scrollArea}>
        <div id="room-list" className={styles.roomList}>
          {visibleRooms.length > 0 ? (
            visibleRooms.map((item: any, index: number) => {
              const isLast = index === visibleRooms.length - 1;
              return (
                <div
                  key={item.id}
                  ref={isLast ? lastElementRef : null}
                  style={{ width: '100%' }}>
                  <button
                    type="button"
                    className={styles.roomButton}
                    onClick={() => changeRoom(item)}>
                    <span className={styles.icon}>
                      <ChatIcon />
                    </span>
                    <span className={styles.name}>{item.name}</span>
                    <div className={styles.countWrapper}>
                      <div className={styles.count}>{item.usersCount ?? 0}</div> <UsersIcon className={styles.userIcon} />
                      <span style={{ display: item.usersCount ? "block" : "none" }} className={styles.indicator}></span>
                    </div>
                  </button>
                </div>
              );
            })
          ) : (
            context.searchQuery?.trim() && (
              <div className={styles.noResults}>Nie znaleziono pokoju...</div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomContainer;